// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "../services/userStorage";
import React, { useState, useEffect } from 'react';
import { Check, ChevronDown, Calendar } from 'lucide-react';

const BibleReadingPlan = ({ theme }) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [readingProgress, setReadingProgress] = useState({});
    const [showPlanDetails, setShowPlanDetails] = useState(false);
    const [availablePlans, setAvailablePlans] = useState([]);

    // Load saved progress from localStorage
    useEffect(() => {
        try {
            const savedProgress = localStorage.getItem('bible_reading_progress');
            if (savedProgress) {
                setReadingProgress(JSON.parse(savedProgress));
            }
        } catch (error) {
            console.error('Error loading reading progress:', error);
        }
    }, []);

    // Generate available plans based on theme or default options
    useEffect(() => {
        // This would ideally come from an API with curated reading plans
        const plans = [
            {
                id: 'psalms30',
                title: 'Psalms in 30 Days',
                description: 'Read through the Book of Psalms in one month',
                duration: 30,
                readings: Array.from({ length: 30 }, (_, i) => ({
                    day: i + 1,
                    reference: `Psalm ${Math.floor(i * 5) + 1}-${Math.floor(i * 5) + 5}`,
                    completed: false
                }))
            },
            {
                id: 'nt90',
                title: 'New Testament in 90 Days',
                description: 'Read through the entire New Testament in three months',
                duration: 90,
                readings: [
                    { day: 1, reference: 'Matthew 1-3', completed: false },
                    { day: 2, reference: 'Matthew 4-6', completed: false },
                    { day: 3, reference: 'Matthew 7-9', completed: false },
                    // In a full implementation, this would include all 90 days
                ]
            },
            {
                id: 'wisdom21',
                title: '21 Days of Wisdom',
                description: 'Focus on wisdom literature for three weeks',
                duration: 21,
                readings: [
                    { day: 1, reference: 'Proverbs 1', completed: false },
                    { day: 2, reference: 'Proverbs 2', completed: false },
                    { day: 3, reference: 'Proverbs 3', completed: false },
                    // And so on for 21 days
                ]
            }
        ];

        // If theme is provided, prioritize relevant plans
        if (theme) {
            const themeLower = theme.toLowerCase();
            // Order plans with theme-relevant ones first
            const orderedPlans = [...plans].sort((a, b) => {
                const aRelevant = a.title.toLowerCase().includes(themeLower) ||
                    a.description.toLowerCase().includes(themeLower);
                const bRelevant = b.title.toLowerCase().includes(themeLower) ||
                    b.description.toLowerCase().includes(themeLower);
                return (bRelevant - aRelevant);
            });
            setAvailablePlans(orderedPlans);
        } else {
            setAvailablePlans(plans);
        }
    }, [theme]);

    // Mark a reading as complete/incomplete
    const toggleReadingCompletion = (planId, day) => {
        if (!selectedPlan) return;

        // Create updated plan with the toggled reading
        const updatedPlan = {
            ...selectedPlan,
            readings: selectedPlan.readings.map(reading =>
                reading.day === day
                    ? { ...reading, completed: !reading.completed }
                    : reading
            )
        };

        setSelectedPlan(updatedPlan);

        // Update progress in state and localStorage
        const updatedProgress = {
            ...readingProgress,
            [planId]: updatedPlan.readings.map(r => ({
                day: r.day,
                completed: r.completed
            }))
        };

        setReadingProgress(updatedProgress);
        localStorage.setItem('bible_reading_progress', JSON.stringify(updatedProgress));
    };

    // Select a reading plan
    const handleSelectPlan = (plan) => {
        // Apply any saved progress to the plan
        if (readingProgress[plan.id]) {
            const savedReadings = readingProgress[plan.id];
            const updatedReadings = plan.readings.map(reading => {
                const savedReading = savedReadings.find(r => r.day === reading.day);
                return savedReading
                    ? { ...reading, completed: savedReading.completed }
                    : reading;
            });

            setSelectedPlan({
                ...plan,
                readings: updatedReadings
            });
        } else {
            setSelectedPlan(plan);
        }

        setShowPlanDetails(true);
    };

    // Calculate completion percentage
    const getCompletionPercentage = (plan) => {
        if (!plan || !plan.readings || plan.readings.length === 0) return 0;

        const completedCount = plan.readings.filter(r => r.completed).length;
        return Math.round((completedCount / plan.readings.length) * 100);
    };

    return (
        <div className="bible-reading-plan">
            {!selectedPlan ? (
                <div>
                    <h3 className="text-lg font-semibold mb-4">Choose a Bible Reading Plan</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {availablePlans.map(plan => (
                            <div
                                key={plan.id}
                                className="border rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition"
                                onClick={() => handleSelectPlan(plan)}
                            >
                                <h4 className="font-medium text-blue-700">{plan.title}</h4>
                                <p className="text-sm text-gray-600 mb-2">{plan.description}</p>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs flex items-center">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        {plan.duration} days
                                    </span>
                                    <button className="btn btn-xs btn-primary">
                                        Select
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">{selectedPlan.title}</h3>
                        <button
                            onClick={() => setSelectedPlan(null)}
                            className="btn btn-xs btn-outline"
                        >
                            Change Plan
                        </button>
                    </div>

                    <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">{selectedPlan.description}</p>
                        <div className="relative pt-1">
                            <div className="flex mb-2 items-center justify-between">
                                <div>
                                    <span className="text-xs font-semibold inline-block text-blue-600">
                                        {getCompletionPercentage(selectedPlan)}% Complete
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="text-xs font-semibold inline-block text-blue-600">
                                        {selectedPlan.readings.filter(r => r.completed).length}/{selectedPlan.readings.length} Days
                                    </span>
                                </div>
                            </div>
                            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
                                <div
                                    style={{ width: `${getCompletionPercentage(selectedPlan)}%` }}
                                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border">
                        <div className="flex justify-between items-center p-3 border-b">
                            <h4 className="font-medium">Reading Schedule</h4>
                            <button
                                onClick={() => setShowPlanDetails(!showPlanDetails)}
                                className="text-blue-600"
                            >
                                <ChevronDown className={`h-5 w-5 transition-transform ${showPlanDetails ? 'rotate-180' : ''}`} />
                            </button>
                        </div>

                        {showPlanDetails && (
                            <div className="p-3">
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {selectedPlan.readings.map(reading => (
                                        <div
                                            key={reading.day}
                                            className={`flex items-center justify-between p-2 rounded ${reading.completed ? 'bg-green-50' : 'bg-gray-50'}`}
                                            onClick={() => toggleReadingCompletion(selectedPlan.id, reading.day)}
                                        >
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={reading.completed}
                                                    onChange={() => toggleReadingCompletion(selectedPlan.id, reading.day)}
                                                    className="form-checkbox h-4 w-4 text-blue-600"
                                                />
                                                <span className="ml-2">Day {reading.day}: {reading.reference}</span>
                                            </div>
                                            {reading.completed && <Check className="h-4 w-4 text-green-500" />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BibleReadingPlan;
